import Image from 'next/image'
import {FC} from 'react'

import {getTrademarkRenewalContent} from '@/content/trademark-renewal/trademarkRenewal'
import {Link, Trans, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {useLanguage} from '@/utils/i18n/language'
import {
  MOBILE_SCREENSIZE,
  TABLET_SCREENSIZE,
  USER_TABLET_SCREENSIZE,
} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {Button} from '../common/content/Button'
import {ButtonVariant} from '../common/content/ButtonTypes'
import {ListItemWithCheckmark} from '../common/content/ListItemWithCheckmark'
import {Section} from '../common/content/Section'
import {SectionLayout} from '../common/content/SectionLayout'
import {ArrowIcon} from '../common/icons/ArrowIcon'
import {CheckCircleOutlineIcon} from '../common/icons/CheckCircleOutlineIcon'
import {useMobile} from '../context/MobileProvider'

export const TmRenewalPortalOverview: FC<React.PropsWithChildren<unknown>> = () => {
  const {t} = useTranslation()
  const language = useLanguage()
  const {title, description, list, buttonLinkText, buttonLinkHref, imageSrc, imageAlt} =
    getTrademarkRenewalContent(language).portalOverview
  const isMobile = useMobile()

  const handleClick = () =>
    analytics.track('Discover cutomer portal is clicked', {
      section: 'Customer portal overview',
      page: 'Trademark renewal',
    })

  return (
    <>
      <Section hasPaddingTop hasPaddingBottom backgroundColor={theme.palette.background.butter}>
        <SectionLayout
          title={{text: t(title), maxWidth: '510px'}}
          description={{text: t(description)}}
          align="left"
          orientation="horizontal"
          buttons={
            isMobile
              ? undefined
              : [
                  {
                    text: t(buttonLinkText),
                    variant: ButtonVariant.default,
                    onClick: handleClick,
                    icon: <ArrowIcon />,
                    linkHref: buttonLinkHref,
                    isHoverable: true,
                  },
                ]
          }
          additionalContent={
            <ul className="list">
              {list.map((item, i) => (
                <ListItemWithCheckmark
                  key={i}
                  text={<Trans i18nKey={item} />}
                  icon={<CheckCircleOutlineIcon color={theme.palette.success.main} />}
                  verticalAlign="middle"
                />
              ))}
            </ul>
          }
          isReversed
          maxWidth="540px"
        >
          <div className="image">
            <Image
              src={imageSrc}
              alt={imageAlt}
              width={940}
              height={520}
              style={{width: '100%', height: 'auto'}}
              sizes={`${MOBILE_SCREENSIZE} 100vw, ${TABLET_SCREENSIZE} 540px, 480px`}
            />
          </div>
          {isMobile && (
            <div className="button-wrapper">
              <Link href={buttonLinkHref} onClick={handleClick}>
                <Button type="button" icon={<ArrowIcon />} isHoverable>
                  {t(buttonLinkText)}
                </Button>
              </Link>
            </div>
          )}
        </SectionLayout>
      </Section>

      <style jsx>{`
        .list {
          margin-top: ${theme.spacing[4]};
        }
        .button-wrapper {
          text-align: center;
          margin-top: ${theme.spacing[6]};
        }
        .image {
          width: 100%;
          max-width: 480px;
          margin-right: ${theme.spacing[6]};
        }
        @media ${TABLET_SCREENSIZE} {
          .button-wrapper {
            margin-top: ${theme.spacing[5]};
          }
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .image {
            max-width: 540px;
            margin-right: 0;
            margin-top: ${theme.spacing[6]};
          }
        }
      `}</style>
    </>
  )
}
